import React, { FC, useEffect, useState } from 'react';
import { useMerchants } from '../../store/merchnts/merchants-context';
import { fetchAccountList } from '../../store/merchnts/actions/account';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PageTitle } from '../../layout/core';
import { GoBackBtn } from '../header/GoBackBtn';
import { TableComponent } from '../TableComponent';
import { SectionWrapper } from '../SectionWrapper';
import { IModalWindow, ModalWindow } from '../modal/ModalWindow';
import { AddNewAccount } from './AddNewAccount';
import { AccountTableRow } from './AccountTableRow';
import { ACCOUNT_COLUMNS } from '../../views/accounts/Accounts';
import { AccountDetailsMerchantsList } from './AccountDetailsMerchantsList';
import { AccountDetailsApplicantsList } from './AccountDetailsApplicantsList';

export const AccountDetails: FC = () => {
	const { merchantState: { accountList, isAccountListLoading }, dispatch } = useMerchants();
	const { t } = useTranslation();
	const { accountId } = useParams();

	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	const closeModal = (): void => setModalState(prevState => ({ ...prevState, isOpen: false }));

	const tableData = accountList?.filter(el => el?.id === accountId);

	const handleSuccess = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: t('ACCOUNT.MODAL_TITLE_EDIT'),
			content: (
				<div className="fs-6">
					{t('ACCOUNT.SUCCESS_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.SUCCESS_BTN_NAME'),
			minHeight: 100,
			onConfirm: closeModal,
			onClose: closeModal
		});
	};

	const handleError = (): void => {
		setIsModalOpen(false);
		setModalState({
			isOpen: true,
			modalTitle: t('ACCOUNT.MODAL_TITLE_EDIT'),
			content: (
				<div className="fs-6">
					{t('ACCOUNT.FAIL_MODAL_TEXT')}
				</div>
			),
			confirmButtonText: t('AGENCY.FAIL_BTN_NAME'),
			minHeight: 100,
			onConfirm: () => {
				closeModal();
				setIsModalOpen(true);
			},
			onClose: closeModal
		});
	};

	return (
		<>
			<PageTitle
				breadcrumbs={[
					{
						title: t('MENU.ACCOUNTS'),
						path: '/account',
						isSeparator: false,
						isActive: false
					},
					{
						title: '',
						path: '',
						isSeparator: true,
						isActive: false
					}
				]}
			>
				{accountId}
			</PageTitle>
			<div className="mb-5">
				<GoBackBtn />
			</div>
			<SectionWrapper
				title={t('ACCOUNT.ACCOUNT_DETAILS')}
				sectionAdditionalClass="mb-5 min-height-200"
				pageLoading={isAccountListLoading}
			>
				<TableComponent
					column={ACCOUNT_COLUMNS}
					tableData={tableData}
					tableMinHeight={tableData.length ? '' : 'table-min-height'}
					getRowComponent={(account): JSX.Element => (
						<AccountTableRow
							{...account}
							key={account?.id}
							onEdit={(): void => setIsModalOpen(true)}
						/>
					)}
				/>
			</SectionWrapper>

			<AccountDetailsApplicantsList tableData={tableData[0]?.applications}/>

			<AccountDetailsMerchantsList tableData={tableData[0]?.merchants}/>

			<ModalWindow
				{...modalState}
				onClose={closeModal}
			/>

			<ModalWindow
				isOpen={isModalOpen}
				backdrop="static"
				onClose={(): void => setIsModalOpen(false)}
				modalTitle={t('ACCOUNT.MODAL_TITLE_EDIT')}
				minHeight={150}
				content={
					<AddNewAccount
						account={tableData[0]}
						onSuccess={handleSuccess}
						onError={handleError}
					/>
				}

			/>

		</>
	);
};
